import { inject, Injectable } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslocoService } from '@jsverse/transloco';

/**
 * Translated labels for every mat-paginator (replaces `SpanishPaginatorIntl`).
 *
 * Registered globally because there is more than one paginator, and two in
 * different languages would be worse than none. The language is fixed for the
 * page lifetime (D2), so labels are read once and no `changes` stream is needed.
 * The `es` output is byte-identical to the former Spanish class (D14).
 */
@Injectable()
export class TranslatedPaginatorIntl extends MatPaginatorIntl {
    private readonly _transloco = inject(TranslocoService);

    override itemsPerPageLabel = this._transloco.translate('common.paginator.itemsPerPage');
    override nextPageLabel = this._transloco.translate('common.paginator.nextPage');
    override previousPageLabel = this._transloco.translate('common.paginator.previousPage');
    override firstPageLabel = this._transloco.translate('common.paginator.firstPage');
    override lastPageLabel = this._transloco.translate('common.paginator.lastPage');

    override getRangeLabel = (page: number, pageSize: number, length: number): string => {
        if (length === 0 || pageSize === 0) {
            return this._transloco.translate('common.paginator.rangeEmpty', { length });
        }

        const safeLength = Math.max(length, 0);
        const startIndex = page * pageSize;

        // A page beyond the end can be requested while a filtered result is
        // still loading; clamp so the label never reads past the total.
        const endIndex =
            startIndex < safeLength
                ? Math.min(startIndex + pageSize, safeLength)
                : startIndex + pageSize;

        return this._transloco.translate('common.paginator.range', {
            start: startIndex + 1,
            end: endIndex,
            length: safeLength,
        });
    };
}
